import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const tooShort = pwd.length > 0 && pwd.length < 8;
  const mismatch = confirm.length > 0 && pwd !== confirm;
  const canSubmit = useMemo(
    () => !!token && pwd.length >= 8 && pwd === confirm && !busy,
    [token, pwd, confirm, busy]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => nav("/login"), 1800);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden" data-testid="reset-password-page"
         style={{ background: "linear-gradient(135deg, hsl(var(--brand-wine)/0.08) 0%, hsl(var(--client)/0.08) 100%)" }}>
      <div className="blob" style={{ width: 320, height: 320, top: -60, right: -60, background: "hsl(var(--client) / 0.18)" }} />
      <div className="blob" style={{ width: 240, height: 240, bottom: -80, left: -40, background: "hsl(var(--brand-lime) / 0.18)" }} />

      <Card className="w-full max-w-md p-7 rounded-3xl border-2 relative z-10" data-testid="reset-password-card">
        <div className="mb-5"><Logo /></div>

        {!token ? (
          <div className="space-y-3" data-testid="reset-password-missing-token">
            <h2 className="text-2xl font-display font-extrabold tracking-tight">Missing reset link</h2>
            <p className="text-sm text-muted-foreground">
              This page needs a reset token. Please use the link from the password-reset email we sent you,
              or request a new one.
            </p>
            <Link to="/forgot-password" className="inline-block">
              <Button className="rounded-full" style={{ background: "hsl(var(--client))" }}>
                Request a new link
              </Button>
            </Link>
          </div>
        ) : done ? (
          <div className="text-center space-y-4 py-2" data-testid="reset-password-success">
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center"
                 style={{ background: "hsl(var(--brand-lime) / 0.25)" }}>
              <CheckCircle2 className="w-8 h-8" style={{ color: "hsl(var(--client))" }} />
            </div>
            <h2 className="text-xl font-display font-extrabold tracking-tight">Password updated</h2>
            <p className="text-sm text-muted-foreground">
              You'll be redirected to the sign-in page in a moment…
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-display font-extrabold tracking-tight">Choose a new password</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Pick something at least 8 characters long. You'll use this to sign in next time.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-password" className="text-xs uppercase tracking-wider font-bold">New password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPwd ? "text" : "password"}
                    required
                    autoFocus
                    data-testid="reset-password-new-input"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    placeholder="At least 8 characters"
                    className="min-h-[48px] rounded-xl pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    data-testid="reset-password-toggle-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {tooShort && (
                  <p className="text-xs text-rose-600" data-testid="reset-password-too-short">
                    Must be at least 8 characters.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirm-password" className="text-xs uppercase tracking-wider font-bold">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type={showPwd ? "text" : "password"}
                  required
                  data-testid="reset-password-confirm-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Type it again"
                  className="min-h-[48px] rounded-xl"
                />
                {mismatch && (
                  <p className="text-xs text-rose-600" data-testid="reset-password-mismatch">
                    Passwords don't match.
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={!canSubmit}
                data-testid="reset-password-submit-btn"
                className="w-full min-h-[52px] rounded-full text-base font-bold"
                style={{ background: "hsl(var(--client))" }}
              >
                {busy ? "Updating…" : "Update password"}
              </Button>
            </form>
          </>
        )}

        <div className="mt-6 pt-4 border-t text-center">
          <Link
            to="/login"
            data-testid="back-to-login-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>
      </Card>
    </div>
  );
}
